import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Trash, Plus, Pencil, Check, X } from 'lucide-react';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const CategoryManagementModal = ({ isOpen, onClose }) => {
    const { categories, addCategory, deleteCategory, updateCategory } = usePIM();
    const { items: sortedCategories, requestSort, sortConfig } = useSortableData(categories);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryDesc, setNewCategoryDesc] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', description: '' });

    const handleAdd = async () => {
        if (!newCategoryName) return;
        await addCategory({ name: newCategoryName, description: newCategoryDesc });
        setNewCategoryName('');
        setNewCategoryDesc('');
    };

    const startEdit = (category) => {
        setEditingId(category.id);
        setEditFormData({ name: category.name, description: category.description || '' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditFormData({ name: '', description: '' });
    };

    const saveEdit = async (id) => {
        await updateCategory(id, editFormData);
        setEditingId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage Categories">
            <div className="space-y-6">
                {/* Add New Category */}
                <div className="flex flex-col gap-3 bg-stone-900 p-4 rounded-lg border border-stone-700">
                    <div className="flex-1 space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Category Name</label>
                        <Input
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="e.g. Smartwatch"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Description</label>
                        <Input
                            value={newCategoryDesc}
                            onChange={(e) => setNewCategoryDesc(e.target.value)}
                            placeholder="Optional description..."
                        />
                    </div>
                    <PrimaryButton onClick={handleAdd} className="h-9 w-full justify-center mt-2">
                        <Plus size={16} className="mr-1" /> Add Category
                    </PrimaryButton>
                </div>

                {/* Category List */}
                <div className="flex-1 overflow-hidden flex flex-col border border-stone-700 rounded-lg">
                    <div className="overflow-y-auto max-h-[400px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-stone-950 [&::-webkit-scrollbar-thumb]:bg-stone-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-stone-600">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-stone-950 text-stone-500 text-xs font-bold uppercase sticky top-0 z-10">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Description" sortKey="description" currentSort={sortConfig} onSort={requestSort} />
                                    <th className="px-4 py-3 border-b border-stone-800 text-right w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800 text-sm text-stone-300 bg-stone-900/50">
                                {sortedCategories.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="p-4 text-center text-stone-500 italic">No categories defined.</td>
                                    </tr>
                                ) : (
                                    sortedCategories.map((category) => (
                                        <tr key={category.id} className="hover:bg-stone-800/50 transition-colors">
                                            {editingId === category.id ? (
                                                <>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.name}
                                                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                            className="h-8 text-xs"
                                                            autoFocus
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.description}
                                                            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                                            placeholder="Description"
                                                            className="h-8 text-xs"
                                                        />
                                                    </td>
                                                    <td className="p-2 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => saveEdit(category.id)}
                                                                className="text-green-500 hover:text-green-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="text-stone-500 hover:text-stone-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="p-3 font-medium text-stone-200">{category.name}</td>
                                                    <td className="p-3 text-stone-400">{category.description}</td>
                                                    <td className="p-3 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => startEdit(category)}
                                                                className="text-stone-500 hover:text-blue-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteCategory(category.id)}
                                                                className="text-stone-500 hover:text-red-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Trash size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
